import { NavLink, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { Tv, Sparkles, Heart, Bookmark, ChevronDown, LogOut, UserRound, KeyRound, Clock, Check } from 'lucide-react';
import { useAuthStore, useUiStore, usePrefsStore } from '../lib/stores.js';
import { authApi } from '../lib/api.js';
import {
  TIMEZONE_OPTIONS,
  browserTimezone,
  currentOffsetLabel,
  formatTimezoneLabel,
  timezoneForCountry,
} from '../lib/time.js';
import styles from './TopBar.module.css';

export default function TopBar() {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const openModal = useUiStore((s) => s.openModal);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    clear();
    navigate('/');
  };

  return (
    <header className={styles.bar}>
      <div className={styles.brand}>
        <Logo />
        <span className={styles.wordmark}>VIBE<span className={styles.tv}> TV</span></span>
      </div>

      <nav className={styles.nav}>
        <NavLink to="/" end className={({ isActive }) => (isActive ? styles.active : '')}>
          <Tv size={16} /> Live
        </NavLink>
        <NavLink to="/premieres" className={({ isActive }) => (isActive ? styles.active : '')}>
          <Sparkles size={16} /> Premieres
        </NavLink>
        <NavLink to="/watchlist" className={({ isActive }) => (isActive ? styles.active : '')}>
          <Bookmark size={16} /> Watchlist
        </NavLink>
        <NavLink to="/favorites" className={({ isActive }) => (isActive ? styles.active : '')}>
          <Heart size={16} /> Favorites
        </NavLink>
      </nav>

      <div className={styles.right}>
        <TimezoneMenu />
        {user ? (
          <UserMenu user={user} onLogout={handleLogout} />
        ) : (
          <>
            <button className="btn ghost" onClick={() => openModal('login')}>Sign in</button>
            <button className="btn primary" onClick={() => openModal('register')}>Create account</button>
          </>
        )}
      </div>
    </header>
  );
}

function TimezoneMenu() {
  const value = usePrefsStore((s) => s.timezone);
  const set = usePrefsStore((s) => s.setTimezone);
  const userCountry = useAuthStore((s) => s.user?.country);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // What "Auto" resolves to right now — the country tz when the user
  // has one, otherwise the browser zone. Shown in the pill so the user
  // knows which clock the EPG is on without opening the menu.
  const countryTz = timezoneForCountry(userCountry);
  const autoTz = countryTz || browserTimezone();

  return (
    <div className={styles.userWrap} ref={ref}>
      <button className={styles.tzBtn} onClick={() => setOpen((o) => !o)} title="EPG times timezone">
        <Clock size={14} />
        <span className={styles.tzLabel}>{shortTimezoneLabel(value || autoTz)}</span>
        <ChevronDown size={13} />
      </button>
      {open && (
        <div className={`${styles.menu} ${styles.tzMenu}`}>
          <button
            className={`${styles.menuItem} ${value === null ? styles.menuItemActive : ''}`}
            onClick={() => { set(null); setOpen(false); }}
          >
            {value === null && <Check size={14} />} Auto ({autoTz})
          </button>
          <div className={styles.menuDivider} />
          {TIMEZONE_OPTIONS.map((tz) => (
            <button
              key={tz}
              className={`${styles.menuItem} ${value === tz ? styles.menuItemActive : ''}`}
              onClick={() => { set(tz); setOpen(false); }}
            >
              {value === tz && <Check size={14} />} {formatTimezoneLabel(tz)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function shortTimezoneLabel(timezone) {
  // Compact label for the pill — city name + offset, e.g. "Budapest UTC+2".
  if (!timezone) return 'Auto';
  const city = timezone.split('/').pop().replace(/_/g, ' ');
  const offset = currentOffsetLabel(timezone);
  if (!offset || offset === 'UTC') return city || 'UTC';
  const compact = offset.replace(/:00$/, '').replace(/^UTC/, 'UTC');
  return `${city} ${compact}`;
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const openModal = useUiStore((s) => s.openModal);

  useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className={styles.userWrap} ref={ref}>
      <button className={styles.userBtn} onClick={() => setOpen((o) => !o)}>
        <div className={styles.avatar}>{(user.name || user.email || '?').slice(0, 1).toUpperCase()}</div>
        <span className={styles.userName}>{user.display_name || user.name || user.email}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className={styles.menu}>
          <div className={styles.menuHeader}>
            <div className={styles.menuName}>{user.display_name || user.name || 'Guest'}</div>
            <div className={styles.menuEmail}>{user.email}</div>
          </div>
          <button className={styles.menuItem} onClick={() => { setOpen(false); openModal('reset'); }}>
            <KeyRound size={14} /> Change password
          </button>
          <button className={styles.menuItem} disabled>
            <UserRound size={14} /> Profile (soon)
          </button>
          <div className={styles.menuDivider} />
          <button className={styles.menuItem} onClick={onLogout}>
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Logo() {
  return (
    <svg width="28" height="28" viewBox="0 0 32 32" aria-hidden>
      <rect width="32" height="32" rx="7" fill="#3b82f6" />
      <path d="M6 9 L12.5 23 L15 23 L21.5 9 L18.5 9 L13.75 19.5 L9 9 Z" fill="#fff" />
      <circle cx="24" cy="20" r="2.5" fill="#fff" />
    </svg>
  );
}
